import { keepSessionAlive } from './api'
import {
  lastAuthenticatedActivityAt,
  lastPersistentSessionRefreshAt,
  recordPersistentSessionRefresh,
  session,
  updateSessionExpiry,
} from './session'

const KEEPALIVE_IDLE_MS = 15 * 60 * 1000
const LEADER_LEASE_MS = 2 * 60 * 1000
const REMEMBER_REFRESH_MS = 12 * 60 * 60 * 1000
const CHECK_INTERVAL_MS = 60 * 1000
const LEADER_KEY = 'ci.auth.keepalive-leader'
const tabId = crypto.randomUUID()

let timer: number | null = null
let running = false
let nextAttemptAt = 0

function leaderLease(): { owner: string; expiresAt: number } | null {
  const value = localStorage.getItem(LEADER_KEY)
  if (!value) return null
  const separator = value.lastIndexOf(':')
  if (separator <= 0) return null
  const owner = value.slice(0, separator)
  const expiresAt = Number(value.slice(separator + 1))
  if (!Number.isFinite(expiresAt)) return null
  return { owner, expiresAt }
}

function acquireLeaderLease(now: number): boolean {
  const lease = leaderLease()
  if (lease !== null && lease.owner !== tabId && lease.expiresAt > now)
    return false

  localStorage.setItem(LEADER_KEY, `${tabId}:${String(now + LEADER_LEASE_MS)}`)
  return leaderLease()?.owner === tabId
}

async function tick(): Promise<void> {
  if (running || !session.token || !navigator.onLine) return
  const now = Date.now()
  if (now < nextAttemptAt) return
  const idleDue = now - lastAuthenticatedActivityAt() >= KEEPALIVE_IDLE_MS
  const rememberRefreshDue =
    session.rememberMe &&
    now - lastPersistentSessionRefreshAt() >= REMEMBER_REFRESH_MS
  if (!idleDue && !rememberRefreshDue) return
  if (!acquireLeaderLease(now)) return

  const tokenUsed = session.token
  running = true
  try {
    const result = await keepSessionAlive()
    updateSessionExpiry(tokenUsed, result.expiresAt)
    if (session.rememberMe) recordPersistentSessionRefresh()
    nextAttemptAt = Date.now() + CHECK_INTERVAL_MS
  } catch {
    nextAttemptAt = Date.now() + CHECK_INTERVAL_MS
  } finally {
    running = false
  }
}

export function startSessionKeepAlive(): () => void {
  if (timer === null) {
    timer = window.setInterval(() => void tick(), CHECK_INTERVAL_MS)
    void tick()
  }

  return () => {
    if (timer !== null) {
      window.clearInterval(timer)
      timer = null
    }
    const lease = leaderLease()
    if (lease?.owner === tabId) localStorage.removeItem(LEADER_KEY)
  }
}
